import { useEffect, useMemo, useRef, useState } from 'react';
import {useSelector} from 'react-redux';
import { URI } from "../../constants";
import {selectAllProductsFromCart} from '../../selectors';

// Город ПВЗ по умолчанию. value = CDEK city_code (137 = Санкт-Петербург).
// Стартуем с одного города, а не всей РФ → виджет не рендерит тысячи маркеров → нет фриза.
const DEFAULT_CITY_CODE = '137';
const DEFAULT_CITY_NAME = 'Санкт-Петербург';

// city_code СДЭК в servicePath → бэк форвардит в /v2/deliverypoints → точки одного города.
const buildServicePath = (cityCode) => `${URI}/api/sdek_service?city_code=${cityCode}`;

// Нормализация названия города для сопоставления геокод ↔ список СДЭК.
const normalizeCity = (name) => (name || '')
  .toLowerCase()
  .replace(/ё/g, 'е')
  .replace(/^(г|город|пос|посёлок|пгт|с|село|д|деревня)[.\s]+/, '')
  .trim();

/**
 * @param {Array<{value:string,label:string}>} sdekCities список городов СДЭК (id=city_code) —
 *        нужен для маппинга названия города из геокодера в city_code.
 */
export const useCDEK = (sdekCities) => {
  const [pvz, setPvz] = useState(null);
  const [ready, setReady] = useState(false);
  const cdekRef = useRef();
  const cartProducts = useSelector(selectAllProductsFromCart);
  const deliveryType = useRef('');

  // eslint-disable-next-line no-undef
  const [CDEK, setCDEK] = useState(null);
  // Какой город уже загружен — чтобы перезагружать ПВЗ только при реальной смене.
  const loadedCityCode = useRef(DEFAULT_CITY_CODE);

  // Map<нормализованное имя города → city_code>. Кладём в ref, чтобы подписка
  // на стор всегда видела актуальный список (он грузится асинхронно).
  const cityCodeByName = useMemo(() => {
    const map = new Map();
    (sdekCities || []).forEach((c) => map.set(normalizeCity(c.label), c.value));
    return map;
  }, [sdekCities]);
  const cityCodeByNameRef = useRef(cityCodeByName);
  cityCodeByNameRef.current = cityCodeByName;

  // Создаём виджет один раз.
  useEffect(() => {
    if (!window.CDEKWidget || CDEK) return;

    setCDEK(new window.CDEKWidget(
      {
        root: 'forpvz',
        defaultLocation: DEFAULT_CITY_NAME,
        from: 'Санкт-Петербург',
        servicePath: buildServicePath(DEFAULT_CITY_CODE),
        apiKey: '26329bb8-426b-4c13-95fe-8a72f2bb521b',
        hideFilters: {
          have_cashless: true,
          have_cash: true,
          is_dressing_room: true,
          type: true,
        },
        tariffs: {
          office: [138],
          door: [139],
          pickup: [366],
        },
        // debug: true,
        onReady() {
          setReady(true);
        },
        onChoose: (type, tariff, address) => {
          setPvz({ price: tariff.delivery_sum, address, type });
          deliveryType.current = type;
        },
        onCalculate(tariff) {
          setPvz((prevState) => ({ ...prevState, price: prevState?.type && tariff[prevState.type].length > 0 ? tariff[prevState.type][0]?.delivery_sum : null }));
        },
      }));
  }, [CDEK]);

  // Город из ВНУТРЕННЕГО поиска виджета (без своего селектора).
  // У виджета нет публичного колбэка на смену города, поэтому подписываемся на его
  // приватный Pinia-стор 'map' (доступен через CDEK.app). На смену центра карты
  // reverse-геокодим центр его же геокодером → locality → city_code → перезагрузка ПВЗ.
  // Хрупко (внутренности Pinia/Vue) → версия виджета запинена в index.html.
  useEffect(() => {
    if (!CDEK || !ready) return undefined;

    const mapStore = CDEK.app?.config?.globalProperties?.$pinia?._s?.get('map');
    if (!mapStore) {
      // eslint-disable-next-line no-console
      console.warn('[CDEK] map store недоступен — авто-смена города по поиску отключена');
      return undefined;
    }

    let timer = null;
    let lastCenterKey = '';

    const resolveCity = async (center) => {
      try {
        const res = await CDEK.yandexApi.geocodeCoordinates(center);
        const member = res?.members?.[0];
        const locality = member?.components?.find((c) => c.kind === 'locality')?.name || member?.name;
        const code = cityCodeByNameRef.current.get(normalizeCity(locality));

        if (!code || code === loadedCityCode.current) return;

        const servicePath = buildServicePath(code);
        CDEK.cdekApi.servicePath = servicePath;
        CDEK.params.servicePath = servicePath;
        CDEK.updateOffices(null); // серверная перезагрузка ПВЗ; getOffices сам отменит предыдущий запрос
        loadedCityCode.current = code;
        setPvz(null);
      } catch (e) {
        // геокод отменён/упал — игнорируем, останется текущий город
      }
    };

    const unsubscribe = mapStore.$subscribe((mutation, state) => {
      const center = state.location?.center;
      if (!Array.isArray(center)) return;
      const key = center.join(',');
      if (key === lastCenterKey) return;
      lastCenterKey = key;

      clearTimeout(timer);
      timer = setTimeout(() => resolveCity(center), 700);
    });

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [CDEK, ready]);

  useEffect(() => {
    if (!cartProducts || cartProducts?.length === 0 || !CDEK) return;

    CDEK.resetParcels();
    CDEK.addParcel(cartProducts.map((p) => ({ width: p?.width ? Math.ceil(p.width) : 1, height: p?.height ? Math.ceil(p?.height) : 1, length: p?.length ? Math.ceil(p.length) : 1, weight: p?.weight ?? 1 })));
  }, [CDEK, cartProducts]);

  return {
    pvz,
    cdekRef,
    setPvz
  };
};
